import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
    Save, 
    Loader2,
    FileText,
    Settings,
    RefreshCw,
    Plus,
    Trash2,
    ChevronDown,
    ChevronUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pages = [
    { 
        slug: 'home', 
        name: 'Homepage',
        sections: [
            { key: 'hero', name: 'Hero Section', fields: ['badge', 'headline', 'subheadline', 'cta_primary', 'cta_secondary', 'image'] },
            { key: 'stats', name: 'Statistics', type: 'array', fields: ['value', 'label'] },
        ]
    },
    { 
        slug: 'about', 
        name: 'About Page',
        sections: [
            { key: 'hero', name: 'Hero Section', fields: ['title', 'subtitle'] },
            { key: 'mission', name: 'Mission Section', fields: ['title', 'content'] },
            { key: 'stats', name: 'Statistics', type: 'array', fields: ['value', 'label'] },
        ]
    },
    {
        slug: 'settings',
        name: 'Site Settings',
        isSettings: true,
        fields: ['company_name', 'tagline', 'email', 'phone', 'whatsapp', 'address']
    }
];

export default function CMSEditor() {
    const { token, getAuthHeader } = useAuth();
    const [activePage, setActivePage] = useState('home');
    const [content, setContent] = useState({});
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expandedSections, setExpandedSections] = useState({});

    useEffect(() => {
        fetchContent();
        fetchSettings();
    }, []);

    const fetchContent = async () => {
        try {
            const response = await axios.get(`${API}/content-all`, getAuthHeader());
            const contentMap = {};
            response.data.forEach(item => {
                if (!contentMap[item.page_slug]) {
                    contentMap[item.page_slug] = {};
                }
                contentMap[item.page_slug][item.section] = item.content;
            });
            setContent(contentMap);
        } catch (error) {
            console.log('No existing content');
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const response = await axios.get(`${API}/settings`);
            setSettings(response.data);
        } catch (error) {
            console.log('Using default settings');
        }
    };

    const handleContentChange = (pageSlug, sectionKey, field, value) => {
        setContent(prev => ({
            ...prev,
            [pageSlug]: {
                ...prev[pageSlug],
                [sectionKey]: {
                    ...(prev[pageSlug]?.[sectionKey] || {}),
                    [field]: value
                }
            }
        }));
    };

    const handleArrayItemChange = (pageSlug, sectionKey, index, field, value) => {
        setContent(prev => {
            const currentArray = prev[pageSlug]?.[sectionKey] || [];
            const newArray = [...currentArray];
            if (!newArray[index]) newArray[index] = {};
            newArray[index][field] = value;
            return {
                ...prev,
                [pageSlug]: {
                    ...prev[pageSlug],
                    [sectionKey]: newArray
                }
            };
        });
    };

    const addArrayItem = (pageSlug, sectionKey, fields) => {
        setContent(prev => {
            const currentArray = prev[pageSlug]?.[sectionKey] || [];
            const newItem = {};
            fields.forEach(f => newItem[f] = '');
            return {
                ...prev,
                [pageSlug]: {
                    ...prev[pageSlug],
                    [sectionKey]: [...currentArray, newItem]
                }
            };
        });
    };

    const removeArrayItem = (pageSlug, sectionKey, index) => {
        setContent(prev => {
            const currentArray = prev[pageSlug]?.[sectionKey] || [];
            return {
                ...prev,
                [pageSlug]: {
                    ...prev[pageSlug],
                    [sectionKey]: currentArray.filter((_, i) => i !== index)
                }
            };
        });
    };

    const handleSettingsChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const saveSection = async (pageSlug, sectionKey) => {
        setSaving(true);
        try {
            await axios.put(
                `${API}/content/${pageSlug}/${sectionKey}`,
                { content: content[pageSlug]?.[sectionKey] || {} },
                getAuthHeader()
            );
            toast.success(`${sectionKey} saved successfully`);
        } catch (error) {
            console.error('Error saving:', error);
            toast.error('Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/settings`, settings, getAuthHeader());
            toast.success('Settings saved successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const toggleSection = (key) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const currentPage = pages.find(p => p.slug === activePage);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div data-testid="cms-editor">
            <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-bold">Content Management</h1>
                <Button 
                    variant="outline" 
                    onClick={() => { fetchContent(); fetchSettings(); }}
                    className="rounded-full"
                >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                </Button>
            </div>

            {/* Page Tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
                {pages.map(page => (
                    <button
                        key={page.slug}
                        onClick={() => setActivePage(page.slug)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                            activePage === page.slug
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                        }`}
                    >
                        {page.isSettings ? <Settings className="w-4 h-4 inline mr-2" /> : <FileText className="w-4 h-4 inline mr-2" />}
                        {page.name}
                    </button>
                ))}
            </div>

            {/* Settings Page */}
            {currentPage?.isSettings ? (
                <div className="p-6 rounded-2xl border border-border bg-background">
                    <h2 className="text-xl font-semibold mb-6">Site Settings</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {currentPage.fields.map(field => (
                            <div key={field} className="space-y-2">
                                <Label htmlFor={field} className="capitalize">
                                    {field.replace(/_/g, ' ')}
                                </Label>
                                <Input
                                    id={field}
                                    value={settings[field] || ''}
                                    onChange={(e) => handleSettingsChange(field, e.target.value)}
                                    placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                                />
                            </div>
                        ))}
                    </div>
                    <Button 
                        onClick={saveSettings}
                        className="mt-6 rounded-full"
                        disabled={saving}
                    >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save Settings
                    </Button>
                </div>
            ) : (
                /* Content Sections */
                <div className="space-y-6">
                    {currentPage?.sections.map(section => (
                        <div key={section.key} className="rounded-2xl border border-border bg-background overflow-hidden">
                            <button
                                onClick={() => toggleSection(section.key)}
                                className="w-full flex items-center justify-between p-6 hover:bg-muted/50 transition-colors"
                            >
                                <h3 className="text-lg font-semibold">{section.name}</h3>
                                {expandedSections[section.key] ? (
                                    <ChevronUp className="w-5 h-5" />
                                ) : (
                                    <ChevronDown className="w-5 h-5" />
                                )}
                            </button>

                            {expandedSections[section.key] && (
                                <div className="p-6 pt-0 border-t border-border">
                                    {section.type === 'array' ? (
                                        /* Array Fields */
                                        <div className="space-y-4">
                                            {(content[currentPage.slug]?.[section.key] || []).map((item, index) => (
                                                <div key={index} className="flex gap-4 items-end p-4 rounded-xl bg-muted/30">
                                                    {section.fields.map(field => (
                                                        <div key={field} className="flex-1 space-y-2">
                                                            <Label className="capitalize text-xs">{field}</Label>
                                                            <Input
                                                                value={item[field] || ''}
                                                                onChange={(e) => handleArrayItemChange(
                                                                    currentPage.slug, 
                                                                    section.key, 
                                                                    index, 
                                                                    field, 
                                                                    e.target.value
                                                                )}
                                                                placeholder={field}
                                                            />
                                                        </div>
                                                    ))}
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => removeArrayItem(currentPage.slug, section.key, index)}
                                                        className="text-destructive hover:text-destructive"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                            <Button
                                                variant="outline"
                                                onClick={() => addArrayItem(currentPage.slug, section.key, section.fields)}
                                                className="rounded-full"
                                            >
                                                <Plus className="w-4 h-4 mr-2" />
                                                Add Item
                                            </Button>
                                        </div>
                                    ) : (
                                        /* Regular Fields */
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {section.fields.map(field => (
                                                <div key={field} className={`space-y-2 ${
                                                    ['content', 'subheadline', 'headline'].includes(field) ? 'md:col-span-2' : ''
                                                }`}>
                                                    <Label htmlFor={`${section.key}-${field}`} className="capitalize">
                                                        {field.replace(/_/g, ' ')}
                                                    </Label>
                                                    {['content', 'subheadline', 'subtitle'].includes(field) ? (
                                                        <Textarea
                                                            id={`${section.key}-${field}`}
                                                            value={content[currentPage.slug]?.[section.key]?.[field] || ''}
                                                            onChange={(e) => handleContentChange(
                                                                currentPage.slug, 
                                                                section.key, 
                                                                field, 
                                                                e.target.value
                                                            )}
                                                            placeholder={`Enter ${field}`}
                                                            rows={4}
                                                        />
                                                    ) : (
                                                        <Input
                                                            id={`${section.key}-${field}`}
                                                            value={content[currentPage.slug]?.[section.key]?.[field] || ''}
                                                            onChange={(e) => handleContentChange(
                                                                currentPage.slug, 
                                                                section.key, 
                                                                field, 
                                                                e.target.value
                                                            )}
                                                            placeholder={`Enter ${field}`}
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <Button 
                                        onClick={() => saveSection(currentPage.slug, section.key)}
                                        className="mt-6 rounded-full"
                                        disabled={saving}
                                    >
                                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                        Save {section.name}
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
